// Pure helpers behind the Reminders page (pages/TimesheetReminders.jsx).
// Kept here so they unit-test without rendering, like lib/usageTable.js.

export const WEEKDAYS = [
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
  { value: '7', label: 'Sunday' },
];

export const HOURS = Array.from({ length: 24 }, (_, hour) => ({
  value: String(hour),
  label: `${String(hour).padStart(2, '0')}:00`,
}));

export function weekdayLabel(value) {
  return WEEKDAYS.find((day) => day.value === String(value))?.label || '';
}

// The settings form state ↔ the API document (numbers stay numbers on the
// wire; Select works in strings).
export function toForm(settings) {
  return {
    enabled: settings.enabled,
    reportOnly: settings.reportOnly,
    sendHour: String(settings.sendHour),
    reminderWeekday: String(settings.reminderWeekday),
    escalationWeekday: String(settings.escalationWeekday),
    lookbackWeeks: String(settings.lookbackWeeks),
    fallbackRecipientIds: settings.fallbackRecipientIds,
  };
}

export function toSettings(form) {
  return {
    enabled: form.enabled,
    reportOnly: form.reportOnly,
    sendHour: Number(form.sendHour),
    reminderWeekday: Number(form.reminderWeekday),
    escalationWeekday: Number(form.escalationWeekday),
    lookbackWeeks: Number(form.lookbackWeeks),
    fallbackRecipientIds: form.fallbackRecipientIds,
  };
}

// 'YYYY-MM-DD' → 'Tue 13 Oct' (UTC, so the label never slides a day).
const DAY_FORMAT = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  weekday: 'short',
  day: 'numeric',
  month: 'short',
});
export function formatDay(iso) {
  const parts = Object.fromEntries(
    DAY_FORMAT.formatToParts(new Date(`${iso}T00:00:00Z`)).map((part) => [
      part.type,
      part.value,
    ]),
  );
  return `${parts.weekday} ${parts.day} ${parts.month}`;
}

// 'YYYY-MM-DD' → 'DD.MM.YYYY', how dates read everywhere else in the app.
export function formatDate(iso) {
  const [year, month, day] = iso.split('-');
  return `${day}.${month}.${year}`;
}

// Holiday exceptions grouped by country, one column per country that an
// active member is on or that has an exception — not all ~200 calendars.
// countries: [{ value, label }] from /admin/holiday-countries; a code it
// lacks (still loading) shows as the code.
export function overridesByCountry(overrides, countries, users) {
  const groups = new Map();
  for (const user of users)
    if (user.active && user.country) groups.set(user.country, []);
  for (const override of overrides) {
    if (!groups.has(override.country)) groups.set(override.country, []);
    groups.get(override.country).push(override);
  }
  const labels = new Map(countries.map((c) => [c.value, c.label]));
  return [...groups]
    .map(([value, list]) => ({
      value,
      label: labels.get(value) || value,
      overrides: list.sort((a, b) => a.date.localeCompare(b.date)),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export const OVERRIDE_KINDS = [
  { value: 'off', label: 'Extra day off' },
  { value: 'working', label: 'Working day (holiday cancelled)' },
];

export function overrideLabel(override) {
  return `${formatDate(override.date)} · ${
    override.observed ? 'day off' : 'working day'
  }${override.name ? ` · ${override.name}` : ''}`;
}

// One line per person for the preview: 'Petra Late · 6 days'.
export function gapLabel(person) {
  const n = person.missing.length;
  return `${person.user.name} · ${n} ${n === 1 ? 'day' : 'days'}`;
}

export const REASON_LABEL = {
  manager: 'team manager',
  no_manager: 'team has no manager',
};
